import React, { useCallback, useEffect, useMemo, useState } from "react"
import Alert from "@mui/material/Alert"
import Button from "@mui/material/Button"
import FormControlLabel from "@mui/material/FormControlLabel"
import Paper from "@mui/material/Paper"
import Switch from "@mui/material/Switch"
import Table from "@mui/material/Table"
import TableBody from "@mui/material/TableBody"
import TableCell from "@mui/material/TableCell"
import TableContainer from "@mui/material/TableContainer"
import TableHead from "@mui/material/TableHead"
import TableRow from "@mui/material/TableRow"
import TextField from "@mui/material/TextField"
import IconButton from "@mui/material/IconButton"
import Tooltip from "@mui/material/Tooltip"
import SaveRoundedIcon from "@mui/icons-material/SaveRounded"
import RestartAltRoundedIcon from "@mui/icons-material/RestartAltRounded"
import CheckCircleRoundedIcon from "@mui/icons-material/CheckCircleRounded"
import ErrorOutlineRoundedIcon from "@mui/icons-material/ErrorOutlineRounded"
import HistoryRoundedIcon from "@mui/icons-material/HistoryRounded"
import RocketLaunchRoundedIcon from "@mui/icons-material/RocketLaunchRounded"
import AddRoundedIcon from "@mui/icons-material/AddRounded"
import CloseRoundedIcon from "@mui/icons-material/CloseRounded"
import ExpandMoreRoundedIcon from "@mui/icons-material/ExpandMoreRounded"
import ExpandLessRoundedIcon from "@mui/icons-material/ExpandLessRounded"
import OpenInNewRoundedIcon from "@mui/icons-material/OpenInNewRounded"
import PageHeading from "components/common/LayoutComponents/PageHeading"
import LoadingScreen from "components/common/LoadingScreen"
import { PERMISSIONS } from "constants/permission"
import { UsePermissionVerification } from "components/customHooks"
import {
  getAppLaunchPopupOnboardingConfig,
  queryAppLaunchPopupStats,
  saveAppLaunchPopupOnboardingConfig,
} from "services/modal"
import { verifyResponse } from "utils/httpRequest"
import { useSnackbar } from "notistack"
import { StyledModal } from "../Modal/Modal.style"
import { buildAppLaunchPopupPreviewUrl } from "utils/platformUrl"

// ---------- Defaults & helpers (mirrors Onboarding.jsx data model) ----------

const shouldHideWhenUpgraded = (stage) => Number(stage?.day || 0) >= 6

const getDefaultOnboardingConfig = () => ({
  globalEnabled: true,
  triggerRules: {
    openDelaySeconds: 8,
  },
  stages: [
    {
      id: "day_0_complete",
      day: 0,
      label: "Day 0",
      enabled: false,
      modalEnabled: true,
      popupType: "content",
      eyebrow: "Day 0",
      title: "Assessment and onboarding handoff",
      subtitle:
        "Members complete the assessment, see their results and plan, take their first innercise, finish onboarding, and land on the dashboard.",
      cta: "",
      destination: "",
      highlights: [
        "Take the assessment",
        "See results + plan",
        "Finish onboarding questionnaire",
      ],
      media: {
        type: "graphic",
        url: "",
        alt: "Personalized NeuroGym onboarding plan",
      },
      notes: "Day 0 is intentionally off — there is no popup on the same day a member signs up.",
      hideWhenUpgraded: false,
    },
    {
      id: "day_1_ai_supercoach",
      day: 1,
      label: "Day 1",
      enabled: true,
      modalEnabled: true,
      popupType: "engagement",
      eyebrow: "AI SuperCoach",
      title: "Chat with your AI SuperCoach",
      subtitle:
        "Chat with your AI SuperCoach to help you achieve your goals or remove any obstacle.",
      cta: "Chat with AI SuperCoach",
      destination: "",
      highlights: ["Goal support", "Obstacle removal", "Daily momentum"],
      media: {
        type: "graphic",
        url: "",
        alt: "AI SuperCoach conversation preview",
      },
      notes:
        "Leave destination blank to use the dynamic AI SuperCoach prompt route from the member app.",
      hideWhenUpgraded: false,
    },
    {
      id: "day_2_goal_science_video",
      day: 2,
      label: "Day 2",
      enabled: true,
      modalEnabled: true,
      popupType: "content",
      eyebrow: "Core Journey",
      title: "Watch the science of goal achievement",
      subtitle:
        "Reinforce why your brain responds to these tools and how to use them for real momentum.",
      cta: "Watch the Video",
      destination: "/programs/recommended",
      highlights: ["Science-backed motivation", "Clear next step", "Build belief"],
      media: {
        type: "graphic",
        url: "",
        alt: "Goal achievement science visual",
      },
      notes: "Update destination when the canonical video URL is finalized.",
      hideWhenUpgraded: false,
    },
    {
      id: "day_3_first_stack_identity",
      day: 3,
      label: "Day 3",
      enabled: true,
      modalEnabled: true,
      popupType: "content",
      eyebrow: "Innercise",
      title: "My First Stack: Unstoppable Identity",
      subtitle:
        "Guide the member into an identity-based innercise to build a strong foundation.",
      cta: "Start My First Stack",
      destination: "/programs/course?programId=4&lessonId=16",
      highlights: ["Identity shift", "Begin innercising", "Confidence building"],
      media: {
        type: "graphic",
        url: "",
        alt: "Unstoppable Identity innercise visual",
      },
      notes: "Uses the closest verified identity-based innercise route currently in the repo.",
      hideWhenUpgraded: false,
    },
    {
      id: "day_4_habit_change_download",
      day: 4,
      label: "Day 4",
      enabled: true,
      modalEnabled: true,
      popupType: "content",
      eyebrow: "Download",
      title: "Get The new science of habit change",
      subtitle:
        "Share the downloadable 2026 habit change resource to deepen understanding and action.",
      cta: "Get the Download",
      destination: "/programs/recommended",
      highlights: [
        "Actionable habit science",
        "Downloadable resource",
        "Reinforce consistency",
      ],
      media: {
        type: "graphic",
        url: "",
        alt: "Habit change resource visual",
      },
      notes: "Update destination when the canonical 2026 download asset URL is finalized.",
      hideWhenUpgraded: false,
    },
    {
      id: "day_5_take_6_calm_circuits",
      day: 5,
      label: "Day 5",
      enabled: true,
      modalEnabled: true,
      popupType: "content",
      eyebrow: "Innercise",
      title: "Take 6: Calm the Circuits",
      subtitle:
        "Help the member regulate stress and create calm before pushing for the next level.",
      cta: "Play Take 6",
      destination: "/programs/course?programId=116&lessonId=434",
      highlights: ["Calm the nervous system", "Build regulation", "Stay grounded"],
      media: {
        type: "graphic",
        url: "",
        alt: "Calm the Circuits innercise visual",
      },
      notes: "Uses the verified Foundational innercises route from the current catalog.",
      hideWhenUpgraded: false,
    },
    {
      id: "day_6_upgrade_cta",
      day: 6,
      label: "Day 6",
      enabled: true,
      modalEnabled: true,
      popupType: "upgrade",
      eyebrow: "Upgrade",
      title: "Unlock the rest of your NeuroGym experience",
      subtitle:
        "If they have not upgraded yet, invite them to unlock more tools, programs, and support.",
      cta: "See Upgrade Options",
      destination: "/subscription?source=onboarding_day_6_upgrade",
      highlights: ["More tools", "More programs", "More support"],
      media: {
        type: "graphic",
        url: "",
        alt: "Expanded NeuroGym membership visual",
      },
      notes: "Skipped automatically when the member is already upgraded.",
      hideWhenUpgraded: true,
    },
    {
      id: "day_7_upgrade_coupon",
      day: 7,
      label: "Day 7",
      enabled: true,
      modalEnabled: true,
      popupType: "upgrade",
      eyebrow: "20% Off",
      title: "Use your 20% off offer and go all in",
      subtitle:
        "If they still have not upgraded, present the stronger final CTA with the coupon offer.",
      cta: "Claim 20% Off",
      destination: "/subscription?source=onboarding_day_7_coupon",
      highlights: ["Final CTA", "20% off offer", "Series completion"],
      media: {
        type: "graphic",
        url: "",
        alt: "Limited time NeuroGym offer visual",
      },
      notes: "Update destination when the final coupon URL or checkout path is finalized.",
      hideWhenUpgraded: true,
    },
  ],
})

const parseConfigValue = (value) => {
  if (!value) return {}
  if (typeof value === "object") return value
  try {
    return JSON.parse(value)
  } catch {
    return {}
  }
}

const normalizeMedia = (value, fallback = {}) => {
  const type = String(value?.type || fallback?.type || "none").trim().toLowerCase()
  return {
    type: ["image", "vimeo", "graphic"].includes(type) ? type : "none",
    url: String(value?.url || fallback?.url || "").trim(),
    alt: String(value?.alt || fallback?.alt || "").trim(),
  }
}

const normalizeConfig = (value) => {
  const parsed = parseConfigValue(value)
  const fallback = getDefaultOnboardingConfig()
  const incomingStages =
    Array.isArray(parsed?.stages) && parsed.stages.length ? parsed.stages : fallback.stages

  return {
    globalEnabled: parsed?.globalEnabled !== false,
    triggerRules: {
      openDelaySeconds: Math.max(
        0,
        Number(
          parsed?.triggerRules?.openDelaySeconds ?? fallback.triggerRules.openDelaySeconds
        ) || 0
      ),
    },
    stages: incomingStages
      .map((item, index) => {
        const fallbackItem =
          fallback.stages.find((stage) => stage.id === String(item?.id || "").trim()) ||
          fallback.stages[index] ||
          fallback.stages[0]
        const rawPopupType = String(item?.popupType || fallbackItem.popupType).trim().toLowerCase()
        // Backward-compat: in the old model a day could be "enabled" but with
        // modalEnabled=false or popupType="none" to suppress the popup. The new
        // model has only one switch (Show this day), so anything that wouldn't
        // have shown a popup before becomes a disabled day to keep behavior
        // identical (e.g. Day 0 must never start showing a popup unexpectedly).
        const wasShowingPopup =
          item?.modalEnabled !== false && rawPopupType !== "none"
        const enabled = item?.enabled !== false && wasShowingPopup
        return {
          id: fallbackItem.id,
          day: fallbackItem.day,
          label: fallbackItem.label,
          enabled,
          modalEnabled: true,
          popupType: rawPopupType === "none" ? "content" : rawPopupType,
          eyebrow: String(item?.eyebrow || fallbackItem.eyebrow).trim(),
          title: String(item?.title || fallbackItem.title).trim(),
          subtitle: String(item?.subtitle || fallbackItem.subtitle).trim(),
          cta: String(item?.cta || fallbackItem.cta).trim(),
          destination: String(item?.destination || fallbackItem.destination).trim(),
          highlights: Array.isArray(item?.highlights)
            ? item.highlights
                .map((highlight) => String(highlight || "").trim())
                .filter(Boolean)
                .slice(0, 6)
            : fallbackItem.highlights,
          media: normalizeMedia(item?.media, fallbackItem.media),
          notes: String(item?.notes || fallbackItem.notes || "").trim(),
          hideWhenUpgraded: shouldHideWhenUpgraded(fallbackItem),
        }
      })
      .sort((a, b) => a.day - b.day),
  }
}

const dateInputValue = (value) => {
  const date = new Date(value)
  const y = date.getFullYear()
  const m = `${date.getMonth() + 1}`.padStart(2, "0")
  const d = `${date.getDate()}`.padStart(2, "0")
  return `${y}-${m}-${d}`
}

const toRangeStartAt = (dateValue) => {
  if (!dateValue) return null
  const parsed = new Date(`${dateValue}T00:00:00`)
  if (Number.isNaN(parsed.getTime())) return null
  return parsed.getTime()
}

const toRangeEndAt = (dateValue) => {
  if (!dateValue) return null
  const parsed = new Date(`${dateValue}T23:59:59`)
  if (Number.isNaN(parsed.getTime())) return null
  return parsed.getTime()
}

const resolveResponseConfig = (response, fallback = null) => {
  const candidates = [
    response?.data?.config,
    response?.config,
    response?.data?.data?.config,
    response?.data?.data,
    response?.data,
    response,
  ]
  for (const candidate of candidates) {
    if (!candidate) continue
    return normalizeConfig(candidate)
  }
  return normalizeConfig(fallback || getDefaultOnboardingConfig())
}

const resolveResponseUpdatedAt = (response) =>
  response?.data?.updatedAt ||
  response?.data?.data?.updatedAt ||
  response?.updatedAt ||
  ""

const formatRelativeTime = (value) => {
  if (!value) return ""
  const then = new Date(value).getTime()
  if (!Number.isFinite(then)) return ""
  const diffMs = Date.now() - then
  const minutes = Math.floor(diffMs / 60000)
  if (minutes < 1) return "just now"
  if (minutes < 60) return `${minutes} min${minutes === 1 ? "" : "s"} ago`
  const hours = Math.floor(minutes / 60)
  if (hours < 24) return `${hours} hour${hours === 1 ? "" : "s"} ago`
  const days = Math.floor(hours / 24)
  if (days < 30) return `${days} day${days === 1 ? "" : "s"} ago`
  return new Date(value).toLocaleDateString()
}

const POPUP_TYPE_OPTIONS = [
  { value: "engagement", label: "Engagement", tone: "tone-engagement" },
  { value: "content", label: "Content", tone: "tone-content" },
  { value: "upgrade", label: "Upgrade", tone: "tone-upgrade" },
]

const MEDIA_TYPE_OPTIONS = [
  { value: "graphic", label: "Generated graphic" },
  { value: "image", label: "Image URL" },
  { value: "vimeo", label: "Vimeo video" },
  { value: "none", label: "No media" },
]

const popupOptionFor = (value) =>
  POPUP_TYPE_OPTIONS.find((opt) => opt.value === String(value || "").toLowerCase()) ||
  POPUP_TYPE_OPTIONS[0]

const dayBadgeTone = (popupType) => {
  switch (String(popupType || "").toLowerCase()) {
    case "engagement":
      return "tone-engagement"
    case "content":
      return "tone-content"
    case "upgrade":
      return "tone-upgrade"
    default:
      return ""
  }
}

// ---------- Day card ----------

function DayCard({ stage, index, defaults, canEdit, onUpdate, onUpdateHighlights, onResetDefaults }) {
  const [showAdvanced, setShowAdvanced] = React.useState(false)
  const [highlightInput, setHighlightInput] = React.useState("")
  const popupOption = popupOptionFor(stage.popupType)
  const isOff = !stage.enabled
  const tone = dayBadgeTone(stage.popupType)
  const defaultStage =
    (defaults?.stages || []).find((s) => s.id === stage.id) || null
  const previewUrl = buildAppLaunchPopupPreviewUrl(stage.day)

  const addHighlight = () => {
    const value = highlightInput.trim()
    if (!value) return
    const next = [...(stage.highlights || []), value].slice(0, 6)
    onUpdateHighlights(index, next.join(", "))
    setHighlightInput("")
  }

  const removeHighlight = (i) => {
    const next = (stage.highlights || []).filter((_, idx) => idx !== i)
    onUpdateHighlights(index, next.join(", "))
  }

  const updateMedia = (key, value) => {
    onUpdate(index, "media", {
      ...(stage.media || {}),
      [key]: value,
    })
  }

  const handleResetDay = () => {
    if (!defaultStage || !onResetDefaults) return
    onResetDefaults(index, defaultStage)
  }

  return (
    <div className={`ws-day-card ${isOff ? "disabled" : ""}`.trim()}>
      <div className={`ws-day-badge ${tone}`.trim()}>
        <div className="day-eyebrow">Day</div>
        <div className="day-num">{stage.day}</div>
      </div>

      <div className="ws-day-body">
        <div className="ws-day-head">
          <div className="ws-day-head-left">
            <div className="ws-day-pill-row">
              <span className={`ws-pill ${isOff ? "tone-off" : "tone-on"}`}>
                {isOff ? "Off" : "Active"}
              </span>
              <Tooltip title="The popup style controls colors and layout. Edit in advanced settings.">
                <span className={`ws-pill ${popupOption.tone}`}>{popupOption.label}</span>
              </Tooltip>
            </div>
            <TextField
              size="small"
              variant="standard"
              fullWidth
              placeholder="Day title (members see this)"
              value={stage.title}
              onChange={(event) => onUpdate(index, "title", event.target.value)}
              disabled={!canEdit}
              InputProps={{
                disableUnderline: true,
                style: { fontSize: 18, fontWeight: 800, color: "var(--cms-text-primary)" },
              }}
              sx={{ mt: 0.5 }}
            />
          </div>

          <div className="ws-day-actions">
            <Tooltip title="Open the member app and force this day's popup with a query parameter">
              <Button
                size="small"
                variant="outlined"
                className="ws-preview-btn"
                href={previewUrl}
                target="_blank"
                rel="noopener noreferrer"
                startIcon={<OpenInNewRoundedIcon fontSize="small" />}
              >
                Preview in app
              </Button>
            </Tooltip>
            {defaultStage && canEdit ? (
              <Tooltip title="Reset this day to the original copy">
                <IconButton size="small" onClick={handleResetDay}>
                  <RestartAltRoundedIcon fontSize="small" />
                </IconButton>
              </Tooltip>
            ) : null}
          </div>
        </div>

        <TextField
          size="small"
          label="Small label above title"
          placeholder="e.g. AI SuperCoach"
          value={stage.eyebrow}
          onChange={(event) => onUpdate(index, "eyebrow", event.target.value)}
          disabled={!canEdit}
        />

        <TextField
          size="small"
          fullWidth
          multiline
          minRows={2}
          label="What members read"
          placeholder="A short, encouraging message that explains this day."
          value={stage.subtitle}
          onChange={(event) => onUpdate(index, "subtitle", event.target.value)}
          disabled={!canEdit}
          helperText="You can personalize copy with merge tags like {{contact.firstname}}."
        />

        <div className="ws-input-grid">
          <TextField
            size="small"
            label="Button text"
            placeholder="e.g. Watch the video"
            value={stage.cta}
            onChange={(event) => onUpdate(index, "cta", event.target.value)}
            disabled={!canEdit}
          />
          <TextField
            className="ws-full"
            size="small"
            label="Where the button takes them"
            placeholder="e.g. /programs/recommended  (leave blank for default)"
            value={stage.destination}
            onChange={(event) => onUpdate(index, "destination", event.target.value)}
            disabled={!canEdit}
            helperText="Use a relative app path or full HTTPS URL. Merge tags also work here, including {{contact.email}}, {{contact.firstname}}, and {{contact.lastname}}."
          />
        </div>

        <div className="ws-media-panel">
          <div className="ws-media-copy">
            <div className="ws-media-title">Popup visual</div>
            <div className="ws-media-subtitle">
              Use a generated graphic, program image, AI coach image, or Vimeo URL to make this day feel richer.
            </div>
          </div>
          <div className="ws-input-grid">
            <TextField
              size="small"
              select
              SelectProps={{ native: true }}
              label="Visual type"
              value={stage.media?.type || "none"}
              onChange={(event) => updateMedia("type", event.target.value)}
              disabled={!canEdit}
            >
              {MEDIA_TYPE_OPTIONS.map((opt) => (
                <option key={`media-${stage.id}-${opt.value}`} value={opt.value}>
                  {opt.label}
                </option>
              ))}
            </TextField>
            <TextField
              size="small"
              label="Alt text"
              placeholder="Describe the visual for accessibility"
              value={stage.media?.alt || ""}
              onChange={(event) => updateMedia("alt", event.target.value)}
              disabled={!canEdit}
            />
            <TextField
              className="ws-full"
              size="small"
              label={stage.media?.type === "vimeo" ? "Vimeo URL or ID" : "Image URL"}
              placeholder={
                stage.media?.type === "vimeo"
                  ? "https://vimeo.com/123456789 or 123456789"
                  : "https://.../program-image.jpg"
              }
              value={stage.media?.url || ""}
              onChange={(event) => updateMedia("url", event.target.value)}
              disabled={!canEdit || ["none", "graphic"].includes(stage.media?.type)}
              helperText={
                stage.media?.type === "graphic"
                  ? "Generated graphics are rendered by the member app, so no URL is needed."
                  : "Use HTTPS image URLs, Vimeo URLs, or bare Vimeo IDs."
              }
            />
          </div>
        </div>

        <div>
          <div
            style={{
              fontSize: 11,
              fontWeight: 800,
              textTransform: "uppercase",
              letterSpacing: "0.06em",
              color: "var(--cms-text-secondary)",
              marginBottom: 6,
            }}
          >
            Bullet highlights · members see up to 6
          </div>
          <div className="ws-highlight-row">
            {(stage.highlights || []).map((highlight, i) => (
              <span className="ws-highlight-chip" key={`hl-${stage.id}-${i}`}>
                <span>{highlight}</span>
                {canEdit && (
                  <IconButton size="small" onClick={() => removeHighlight(i)} aria-label="Remove">
                    <CloseRoundedIcon style={{ fontSize: 14 }} />
                  </IconButton>
                )}
              </span>
            ))}
            {canEdit && (stage.highlights || []).length < 6 && (
              <>
                <TextField
                  size="small"
                  variant="standard"
                  placeholder="Add a highlight…"
                  value={highlightInput}
                  onChange={(event) => setHighlightInput(event.target.value)}
                  onKeyDown={(event) => {
                    if (event.key === "Enter") {
                      event.preventDefault()
                      addHighlight()
                    }
                  }}
                  sx={{ minWidth: 180 }}
                />
                <button
                  type="button"
                  className="ws-highlight-add"
                  onClick={addHighlight}
                >
                  <AddRoundedIcon style={{ fontSize: 14 }} /> Add
                </button>
              </>
            )}
          </div>
        </div>

        <div className="ws-toggle-row">
          <FormControlLabel
            className={`ws-toggle-chip ${stage.enabled ? "is-on" : "is-off"}`}
            control={
              <Switch
                size="small"
                checked={!!stage.enabled}
                onChange={(_, checked) => onUpdate(index, "enabled", checked)}
                disabled={!canEdit}
              />
            }
            label="Show this day"
          />
          <span className="ws-toggle-helper">
            Every active day shows as a popup and is automatically skipped for paying members.
          </span>
        </div>

        <div className="ws-advanced">
          <button
            type="button"
            className="ws-advanced-toggle"
            onClick={() => setShowAdvanced((v) => !v)}
          >
            {showAdvanced ? <ExpandLessRoundedIcon fontSize="small" /> : <ExpandMoreRoundedIcon fontSize="small" />}
            {showAdvanced ? "Hide advanced settings" : "Show advanced settings"}
          </button>
          {showAdvanced && (
            <div className="ws-advanced-grid">
              <TextField
                size="small"
                label="Internal note (your team only)"
                multiline
                minRows={1}
                value={stage.notes || ""}
                onChange={(event) => onUpdate(index, "notes", event.target.value)}
                disabled={!canEdit}
              />
              <TextField
                size="small"
                select
                SelectProps={{ native: true }}
                label="Popup style"
                value={stage.popupType}
                onChange={(event) => onUpdate(index, "popupType", event.target.value)}
                disabled={!canEdit}
              >
                {POPUP_TYPE_OPTIONS.map((opt) => (
                  <option key={`popup-${stage.id}-${opt.value}`} value={opt.value}>
                    {opt.label}
                  </option>
                ))}
              </TextField>
            </div>
          )}
        </div>
      </div>
    </div>
  )
}

// ---------- Page ----------

export default function OnboardingSeries() {
  const { enqueueSnackbar } = useSnackbar()
  const AccessControl = UsePermissionVerification()

  const defaults = useMemo(() => getDefaultOnboardingConfig(), [])
  const [config, setConfig] = useState(getDefaultOnboardingConfig())
  const [savedConfig, setSavedConfig] = useState(getDefaultOnboardingConfig())
  const [updatedAt, setUpdatedAt] = useState("")
  const [loading, setLoading] = useState(true)
  const [saving, setSaving] = useState(false)
  const [activeStageId, setActiveStageId] = useState(null)

  const [statsLoading, setStatsLoading] = useState(false)
  const [stats, setStats] = useState([])
  const [stageStats, setStageStats] = useState([])
  const [statsSummary, setStatsSummary] = useState({
    totalSeen: 0,
    totalClicked: 0,
    totalDismissed: 0,
    totalCtr: 0,
    totalCurrentUsers: 0,
    totalReachedUsers: 0,
    totalDropoffUsers: 0,
    totalBuyers: 0,
  })
  const [statsStartDate, setStatsStartDate] = useState(
    dateInputValue(Date.now() - 30 * 24 * 60 * 60 * 1000)
  )
  const [statsEndDate, setStatsEndDate] = useState(dateInputValue(Date.now()))

  const canView =
    AccessControl.VerifyAccess([PERMISSIONS.MODAL.MODAL_LIST]) ||
    AccessControl.VerifyAccess([PERMISSIONS.MODAL.MODAL_EDIT])
  const canEdit =
    AccessControl.VerifyAccess([PERMISSIONS.MODAL.MODAL_EDIT]) ||
    AccessControl.VerifyAccess([PERMISSIONS.MODAL.MODAL_ADD])

  const loadConfig = useCallback(async () => {
    if (!canView) {
      setLoading(false)
      return
    }
    setLoading(true)
    try {
      const response = await getAppLaunchPopupOnboardingConfig()
      verifyResponse(response)
      const next = resolveResponseConfig(response, getDefaultOnboardingConfig())
      setConfig(next)
      setSavedConfig(next)
      setUpdatedAt(resolveResponseUpdatedAt(response))
    } catch {
      enqueueSnackbar("Failed to load welcome series config.", {
        variant: "warning",
      })
    } finally {
      setLoading(false)
    }
  }, [canView, enqueueSnackbar])

  const refreshStats = useCallback(
    async (dateRange = null) => {
      if (!canView) return
      const range = dateRange || {
        startAt: toRangeStartAt(statsStartDate),
        endAt: toRangeEndAt(statsEndDate),
      }
      setStatsLoading(true)
      try {
        const response = await queryAppLaunchPopupStats(range)
        verifyResponse(response)
        setStats(response?.data?.list || [])
        setStageStats(response?.data?.stageList || [])
        setStatsSummary({
          totalSeen: Number(response?.data?.totalSeen || 0),
          totalClicked: Number(response?.data?.totalClicked || 0),
          totalDismissed: Number(response?.data?.totalDismissed || 0),
          totalCtr: Number(response?.data?.totalCtr || 0),
          totalCurrentUsers: Number(response?.data?.totalCurrentUsers || 0),
          totalReachedUsers: Number(response?.data?.totalReachedUsers || 0),
          totalDropoffUsers: Number(response?.data?.totalDropoffUsers || 0),
          totalBuyers: Number(response?.data?.totalBuyers || 0),
        })
      } catch {
        enqueueSnackbar("Failed to load welcome series analytics.", {
          variant: "warning",
        })
        setStats([])
        setStageStats([])
        setStatsSummary({
          totalSeen: 0,
          totalClicked: 0,
          totalDismissed: 0,
          totalCtr: 0,
          totalCurrentUsers: 0,
          totalReachedUsers: 0,
          totalDropoffUsers: 0,
          totalBuyers: 0,
        })
      } finally {
        setStatsLoading(false)
      }
    },
    [canView, enqueueSnackbar, statsEndDate, statsStartDate]
  )

  useEffect(() => {
    loadConfig()
    refreshStats({
      startAt: toRangeStartAt(statsStartDate),
      endAt: toRangeEndAt(statsEndDate),
    })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loadConfig])

  const updateStage = (index, key, value) => {
    setConfig((previous) => ({
      ...previous,
      stages: previous.stages.map((stage, stageIndex) =>
        stageIndex === index ? { ...stage, [key]: value } : stage
      ),
    }))
  }

  const updateStageHighlights = (index, value) => {
    updateStage(
      index,
      "highlights",
      String(value || "")
        .split(",")
        .map((item) => item.trim())
        .filter(Boolean)
    )
  }

  const resetStageToDefault = (index, defaultStage) => {
    setConfig((previous) => ({
      ...previous,
      stages: previous.stages.map((stage, stageIndex) =>
        stageIndex === index ? { ...defaultStage } : stage
      ),
    }))
  }

  const handleSave = async () => {
    if (!canEdit) return
    setSaving(true)
    try {
      const payload = {
        globalEnabled: !!config?.globalEnabled,
        triggerRules: {
          openDelaySeconds: Math.max(0, Number(config?.triggerRules?.openDelaySeconds || 0)),
        },
        stages: (config?.stages || []).map((stage) => ({
          id:
            (defaults?.stages || []).find((defaultStage) => defaultStage.id === stage?.id)?.id ||
            String(stage?.id || "").trim(),
          day:
            (defaults?.stages || []).find((defaultStage) => defaultStage.id === stage?.id)?.day ??
            Math.max(0, Number(stage?.day || 0)),
          label:
            (defaults?.stages || []).find((defaultStage) => defaultStage.id === stage?.id)?.label ||
            String(stage?.label || "").trim(),
          enabled: !!stage?.enabled,
          modalEnabled: true,
          popupType: String(stage?.popupType || "content").trim() || "content",
          eyebrow: String(stage?.eyebrow || "").trim(),
          title: String(stage?.title || "").trim(),
          subtitle: String(stage?.subtitle || "").trim(),
          cta: String(stage?.cta || "").trim(),
          destination: String(stage?.destination || "").trim(),
          highlights: Array.isArray(stage?.highlights)
            ? stage.highlights.map((item) => String(item || "").trim()).filter(Boolean)
            : [],
          media: normalizeMedia(stage?.media),
          notes: String(stage?.notes || "").trim(),
          hideWhenUpgraded: shouldHideWhenUpgraded(stage),
        })),
      }
      const response = await saveAppLaunchPopupOnboardingConfig(payload)
      verifyResponse(response)
      const next = resolveResponseConfig(response, payload)
      setConfig(next)
      setSavedConfig(next)
      setUpdatedAt(resolveResponseUpdatedAt(response))
      enqueueSnackbar("Welcome series saved.", { variant: "success" })
    } catch {
      enqueueSnackbar("Failed to save welcome series.", { variant: "warning" })
    } finally {
      setSaving(false)
    }
  }

  const handleDiscard = () => setConfig(savedConfig)

  const isDirty = useMemo(
    () => JSON.stringify(config) !== JSON.stringify(savedConfig),
    [config, savedConfig]
  )

  const seriesEnabled = !!config?.globalEnabled
  const stages = config?.stages || []
  const activeDays = stages.filter((s) => s.enabled).length

  const statsByVariant = useMemo(() => {
    return (stats || []).reduce((acc, item) => {
      acc[item.popupVariant] = item
      return acc
    }, {})
  }, [stats])

  const stageStatsById = useMemo(() => {
    return (stageStats || []).reduce((acc, item) => {
      acc[item.stageId] = item
      return acc
    }, {})
  }, [stageStats])

  return (
    <StyledModal>
      <PageHeading>
        <div className="heading-copy">
          <h1 className="page-title">Welcome Series</h1>
          <p className="page-subtitle">
            The day-by-day messages new members see during their first 8 days. Edit the title,
            wording, and where each button takes them — no code required.
          </p>
        </div>
      </PageHeading>

      <div className="page-surface">
        {!canView ? (
          <div className="empty-row">You do not have permission to view this section.</div>
        ) : (
          <div className="onboarding-panel">
            <div className="ws-hero">
              <h2 className="ws-hero-title">
                <RocketLaunchRoundedIcon /> 8 days. 8 nudges. One smooth start.
              </h2>
              <p className="ws-hero-sub">
                When someone joins NeuroGym, they get a friendly message every day for their first
                week. Each message tells them what to do next — chat with the AI SuperCoach, watch a
                video, try an innercise, or upgrade. You're in charge of every word, button, and
                destination.
              </p>
              <div className="ws-hero-stats">
                <span className="ws-hero-stat-chip">
                  <span className={`ws-pulse-dot ${seriesEnabled ? "" : "off"}`} />
                  {seriesEnabled ? "Series is live" : "Series is paused"}
                </span>
                <span className="ws-hero-stat-chip">
                  {activeDays} / {stages.length} days active
                </span>
                <span className="ws-hero-stat-chip">
                  Open delay {config?.triggerRules?.openDelaySeconds || 0}s
                </span>
                {updatedAt ? (
                  <span className="ws-hero-stat-chip">
                    <HistoryRoundedIcon style={{ fontSize: 14 }} />
                    Updated {formatRelativeTime(updatedAt)}
                  </span>
                ) : null}
              </div>
            </div>

            <Alert severity="info" style={{ marginTop: 4 }}>
              The <strong>Onboarding Modal Series enabled</strong> beta flag in <em>Beta Features</em>
              {" "}controls whether the member app is allowed to show this series at all. The settings
              below control what each day says.
            </Alert>

            <div className="ws-section-title">Series controls</div>
            <div className="ws-control-card">
              <div className="ws-control-toggle-block">
                <h4>
                  <span className={`ws-pulse-dot ${seriesEnabled ? "" : "off"}`} />
                  Welcome series is {seriesEnabled ? "ON" : "OFF"}
                </h4>
                <p>
                  When this is on, new members start receiving the day-by-day messages from the day
                  they sign up. Turn it off to pause the entire series for everyone.
                </p>
                <div className="ws-control-toggle-row">
                  <span style={{ fontWeight: 700, color: "var(--cms-text-primary)" }}>
                    {seriesEnabled ? "Series is running for new members" : "Series is paused"}
                  </span>
                  <Switch
                    checked={seriesEnabled}
                    onChange={(_, checked) =>
                      setConfig((previous) => ({ ...previous, globalEnabled: checked }))
                    }
                    disabled={!canEdit}
                  />
                </div>
              </div>
              <div className="ws-control-delay">
                <h4>How long should we wait before the popup appears?</h4>
                <p>
                  After a member opens the app, we'll wait this many seconds before the day's popup
                  shows up — give them a moment to see the dashboard first.
                </p>
                <TextField
                  size="small"
                  label="Open delay (seconds)"
                  type="number"
                  inputProps={{ min: 0 }}
                  value={config?.triggerRules?.openDelaySeconds}
                  onChange={(event) =>
                    setConfig((previous) => ({
                      ...previous,
                      triggerRules: {
                        ...previous.triggerRules,
                        openDelaySeconds: event.target.value,
                      },
                    }))
                  }
                  disabled={!canEdit}
                />
              </div>
            </div>

            <div className="ws-section-title">Eight days at a glance</div>
            <div className="ws-glance">
              {stages.map((stage) => (
                <div
                  key={`glance-${stage.id}`}
                  className={`ws-glance-pill ${activeStageId === stage.id ? "is-active" : ""} ${stage.enabled ? "" : "is-off"}`.trim()}
                  onClick={() => {
                    setActiveStageId(stage.id)
                    const el = document.getElementById(`day-card-${stage.id}`)
                    if (el && typeof el.scrollIntoView === "function") {
                      el.scrollIntoView({ behavior: "smooth", block: "start" })
                    }
                  }}
                  title={stage.title}
                >
                  <div className="label">Day {stage.day}</div>
                  <div className="value">{stage.eyebrow || stage.title || "—"}</div>
                </div>
              ))}
            </div>

            <div className="ws-section-title">The eight days</div>
            <div className="ws-day-list">
              {stages.map((stage, index) => (
                <div id={`day-card-${stage.id}`} key={`day-card-${stage.id}-${index}`}>
                  <DayCard
                    stage={stage}
                    index={index}
                    defaults={defaults}
                    canEdit={canEdit}
                    onUpdate={updateStage}
                    onUpdateHighlights={updateStageHighlights}
                    onResetDefaults={resetStageToDefault}
                  />
                </div>
              ))}
            </div>

            <div className="ws-section-title">How members are doing</div>
            <div className="ws-control-card" style={{ display: "flex", flexWrap: "wrap" }}>
              <div style={{ flex: 1, minWidth: 200 }}>
                <h4
                  style={{
                    margin: 0,
                    color: "var(--cms-text-primary)",
                    fontSize: 15,
                    fontWeight: 800,
                  }}
                >
                  Performance
                </h4>
                <p style={{ margin: "4px 0 0", color: "var(--cms-text-secondary)", fontSize: 12.5 }}>
                  See how many members reached each day, how many dropped off, and which messages
                  drove clicks and upgrades.
                </p>
              </div>
              <div className="stats-filter-row">
                <TextField
                  size="small"
                  label="From"
                  type="date"
                  value={statsStartDate}
                  onChange={(event) => setStatsStartDate(event.target.value)}
                  InputLabelProps={{ shrink: true }}
                />
                <TextField
                  size="small"
                  label="To"
                  type="date"
                  value={statsEndDate}
                  onChange={(event) => setStatsEndDate(event.target.value)}
                  InputLabelProps={{ shrink: true }}
                />
                <Button
                  variant="outlined"
                  size="small"
                  onClick={() =>
                    refreshStats({
                      startAt: toRangeStartAt(statsStartDate),
                      endAt: toRangeEndAt(statsEndDate),
                    })
                  }
                  disabled={statsLoading}
                >
                  Refresh
                </Button>
              </div>
            </div>

            <div className="ws-perf-summary">
              <div className="ws-perf-card">
                <div className="label">Members in series</div>
                <div className="value">{statsSummary.totalCurrentUsers}</div>
                <div className="sub">currently between day 0 and 7</div>
              </div>
              <div className="ws-perf-card">
                <div className="label">Total reached</div>
                <div className="value">{statsSummary.totalReachedUsers}</div>
                <div className="sub">across all days in range</div>
              </div>
              <div className="ws-perf-card">
                <div className="label">Dropped off</div>
                <div className="value">{statsSummary.totalDropoffUsers}</div>
                <div className="sub">stopped opening the app</div>
              </div>
              <div className="ws-perf-card">
                <div className="label">Buyers</div>
                <div className="value">{statsSummary.totalBuyers}</div>
                <div className="sub">upgraded during the series</div>
              </div>
              <div className="ws-perf-card">
                <div className="label">Popups seen</div>
                <div className="value">{statsSummary.totalSeen}</div>
              </div>
              <div className="ws-perf-card">
                <div className="label">Clicked</div>
                <div className="value">{statsSummary.totalClicked}</div>
              </div>
              <div className="ws-perf-card">
                <div className="label">Dismissed</div>
                <div className="value">{statsSummary.totalDismissed}</div>
              </div>
              <div className="ws-perf-card">
                <div className="label">Click-through</div>
                <div className="value">{statsSummary.totalCtr}%</div>
                <div className="sub">of all popups shown</div>
              </div>
            </div>

            <TableContainer component={Paper}>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Day</TableCell>
                    <TableCell align="right">In day</TableCell>
                    <TableCell align="right">Reached</TableCell>
                    <TableCell align="right">Dropped</TableCell>
                    <TableCell align="right">Buyers</TableCell>
                    <TableCell align="right">Popup seen</TableCell>
                    <TableCell align="right">Clicked</TableCell>
                    <TableCell align="right">Dismissed</TableCell>
                    <TableCell align="right">CTR</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {stages.map((stage) => {
                    const stat = statsByVariant[stage.id] || {
                      seen: 0,
                      clicked: 0,
                      dismissed: 0,
                      ctr: 0,
                    }
                    const stageMetric = stageStatsById[stage.id] || {
                      currentUsers: 0,
                      reachedUsers: 0,
                      dropoffUsers: 0,
                      buyers: 0,
                    }
                    return (
                      <TableRow key={`stage-analytics-${stage.id}`}>
                        <TableCell>
                          <strong>Day {stage.day}</strong> · {stage.title || stage.label}
                        </TableCell>
                        <TableCell align="right">{stageMetric.currentUsers}</TableCell>
                        <TableCell align="right">{stageMetric.reachedUsers}</TableCell>
                        <TableCell align="right">{stageMetric.dropoffUsers}</TableCell>
                        <TableCell align="right">{stageMetric.buyers}</TableCell>
                        <TableCell align="right">{stat.seen}</TableCell>
                        <TableCell align="right">{stat.clicked}</TableCell>
                        <TableCell align="right">{stat.dismissed}</TableCell>
                        <TableCell align="right">{stat.ctr}%</TableCell>
                      </TableRow>
                    )
                  })}
                </TableBody>
              </Table>
            </TableContainer>

            {canEdit && (
              <div className="welcome-save-bar">
                <div className={`save-status ${isDirty ? "dirty" : "clean"}`}>
                  {isDirty ? (
                    <>
                      <ErrorOutlineRoundedIcon fontSize="small" />
                      <span>You have unsaved changes</span>
                    </>
                  ) : (
                    <>
                      <CheckCircleRoundedIcon fontSize="small" />
                      <span>All changes saved</span>
                    </>
                  )}
                </div>
                <div style={{ display: "flex", gap: 8 }}>
                  <Button
                    size="small"
                    variant="text"
                    onClick={handleDiscard}
                    disabled={!isDirty || saving || loading}
                  >
                    Discard changes
                  </Button>
                  <Button
                    size="small"
                    variant="contained"
                    startIcon={<SaveRoundedIcon />}
                    onClick={handleSave}
                    disabled={!isDirty || saving || loading}
                  >
                    {saving ? "Saving…" : "Save changes"}
                  </Button>
                </div>
              </div>
            )}
          </div>
        )}
      </div>

      <LoadingScreen loading={loading || saving || statsLoading} />
    </StyledModal>
  )
}
